<template>
  <div id="datavisualisation" class>
    <div class="header-section">
      <div class="tabs-selector-ctn">
        <b-button
          class="tab-selector"
          :class="{ active: tabName === 'datasheet' }"
          variant="outline-*"
          @click="tabName = 'datasheet'"
        >
          DATA SHEET
        </b-button>
        <b-button
          class="tab-selector"
          :class="{ active: tabName === 'graphic' }"
          variant="outline-*"
          @click="tabName = 'graphic'"
          >GRAPHIC OVERVIEW</b-button
        >
      </div>
      <div class="alloys-ctn">
        <AlloyItem
          v-for="(alloy, index) in selectedAlloys"
          :key="
            alloy.brandName +
              alloy.temper +
              alloy.isoDesignation +
              index +
              '_selected'
          "
          :alloy="alloy"
          class="itemAlloy"
          :is-selected="true"
          :color="graphicColors[index]"
          :alloy-from-visualization="true"
        />
        <div
          v-for="n in 5 - selectedAlloys.length"
          :key="n"
          class="empty-alloy-item"
        >
          <div class="dashed"></div>
        </div>
      </div>
    </div>
    <div v-if="tabName === 'graphic'" class="graphic-wrapper">
      <div class="d-flex flex-column">
        <div class="title mb-5 mt-3">
          <img class="filter-arrow mr-3" src="@/assets/filterArrow.png" alt />
          <p class="filter-name text-uppercase">
            Choose a couple of properties
          </p>
        </div>
        <!-- switch MPa/Ksi -->
        <div class="switch-header row mx-0 mb-3">
          <span>
            MPa
            <b-form-checkbox
              v-model="selectedUnitBool"
              name="check-button"
              switch
              class="unit-switch"
              @change="selectUnit(selectedUnitBool)"
            >
            </b-form-checkbox
            >ksi
          </span>
        </div>
        <!-- couples -->
        <b-form-group>
          <b-form-radio
            v-for="(couple, index) in couplesList"
            :key="index"
            v-model="selected"
            name="some-radios"
            plain
            class="my-radio mb-3"
            :class="selected == index ? 'bold' : null"
            :value="index"
          >
            {{
              couple.prop1.name +
                (couple.prop1.MPaKSI ? ` (${selectedUnit})` : "") +
                " / " +
                couple.prop2.name +
                (couple.prop2.MPaKSI ? ` (${selectedUnit})` : "")
            }}
          </b-form-radio>
        </b-form-group>
        <div class="mt-auto">
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!selectedAlloys.length"
            @click="exportPDF()"
          >
            Export PDF
          </button>
          <small v-if="isLoading" class="ml-3 py-2 position-absolute"
            >Generation in progress...</small
          >
          <small class="d-block mt-2">
            Create PDF file of the graphic overview for the selected items
          </small>
        </div>
      </div>
      <div>
        <GraphicWithCouple
          :id="'main_chart'"
          :couple="selectedCouple"
          :selected-unit="selectedUnit"
          :selected-alloys="selectedAlloys"
          :width="500"
          :height="500"
        />
        <small class="text-secondary mx-auto mt-3">
          &#9432; Dots are placed at 0 when no data was available for one
          parameter
        </small>
      </div>
    </div>
    <div v-else-if="tabName === 'datasheet'" id="datasheet-section">
      <img
        src="@/assets/arrow.png"
        alt
        class="d-none d-md-block arrowUp"
        @click="scroll('up')"
      />
      <DataSheet :alloys="selectedAlloys" class="dataSheet-wrapper" />
      <img
        src="@/assets/arrow.png"
        alt
        class="d-none d-md-block arrowDown"
        @click="scroll('down')"
      />
    </div>

    <VueHtml2pdf
      ref="html2Pdf"
      :show-layout="false"
      :preview-modal="true"
      :paginate-elements-by-height="1400"
      :filename="getName()"
      :pdf-quality="2"
      pdf-format="a4"
      pdf-orientation="landscape"
      pdf-content-width="100%"
      @progress="onProgress($event)"
      @hasStartedDownload="hasStartedGeneration()"
    >
      <section slot="pdf-content" class="main-container-pdf">
        <section class="header py-2">
          <img id="logo" src="@/assets/logoNavigator.png" alt="logo LBA" />
          <div class="my-auto">
            <p class="text-right mb-0">{{ store.username }}</p>
            <small>{{ currentTime }}</small>
          </div>
        </section>
        <section class="message-ctn">
          <p class="mb-0">
            <strong
              >Thanks for using our material navigator, here is the result of
              your selection :</strong
            >
          </p>
          <small
            >Disclaimer: all information for guidance only, non contractual
            data.</small
          >
        </section>
        <section class="central-ctn">
          <div class="alloys-ctn-pdf">
            <AlloyItem
              v-for="(alloy, index) in selectedAlloys"
              :key="index + '_export_item'"
              :alloy="alloy"
              class="itemAlloy"
              :show-informations="true"
              :dark-text="true"
              :color="graphicColors[index]"
              :alloy-from-visualization="true"
            />
          </div>
          <div class="graphs-ctn">
            <div
              v-for="(couple, i) in couplesList"
              :key="'chart_' + i"
              class="graph-ctn"
            >
              <p class="font-weight-bold my-2">
                {{ couple.prop1.name }} / {{ couple.prop2.name }}
              </p>
              <GraphicWithCouple
                :id="'chart_' + i"
                :couple="couple"
                :selected-unit="selectedUnit"
                :selected-alloys="selectedAlloys"
                :white-bdg="true"
                :show-legend="false"
                :rayon="5"
                :adaptative-ctn="true"
                :font-size="9"
              />
            </div>
            <small class="text-secondary mx-auto"
              >&#9432; Dots are placed at 0 when no data was available for one
              parameter</small
            >
          </div>
        </section>
        <section class="footer">
          <div class="py-2">
            Copyright &copy;
            <a class="text-white" href="https://www.lebronze-alloys.com/fr/">
              <u>LeBronze Alloys</u>
            </a>
          </div>
        </section>
      </section>
    </VueHtml2pdf>
  </div>
</template>

<script>
import GraphicWithCouple from "@/components/DataVisualization/GraphicWithCouple.vue";
import DataSheet from "@/components/DataVisualization/DataSheet.vue";
import Couples from "@/config/couples.js";
import AlloyItem from "@/components/AlloyItem";
import $ from "jquery";
import Store from "@/store";
import VueHtml2pdf from "vue-html2pdf";
import axios from "axios";

export default {
  name: "DataVisualization",
  components: {
    DataSheet,
    AlloyItem,
    VueHtml2pdf,
    GraphicWithCouple
  },
  props: {
    selectedAlloys: {
      type: Array,
      default: null
    }
  },
  data: () => {
    return {
      selected: 0,
      couplesList: JSON.parse(JSON.stringify(Couples)),
      tabName: "datasheet",
      selectedUnit: "MPa",
      selectedUnitBool: false,
      graphicColors: ["#ade4d9", "#85d6a8", "#43a19b", "#57BFFA", "#B69BDF"],
      activeAlloy: {},
      isVisible: false,
      store: Store.state,
      currentTime: "",
      isLoading: false
    };
  },
  computed: {
    selectedCouple: function() {
      return this.couplesList[this.selected];
    }
  },
  methods: {
    clickOnAlloy(alloy) {
      this.$parent.addSelectedAlloy(alloy);
    },
    selectUnit: function(selection) {
      this.selectedUnit = selection ? "MPa" : "ksi";
    },
    processNumber: function(stringValue) {
      if (!stringValue || stringValue == "-") return "";
      // if format of value is XXX-XXX
      if (/^[\d,]+[^\d,]+[\d,]+$/.test(stringValue))
        return Number.parseInt(stringValue);
      // to get rid of every non digit character except ','
      let digitString = stringValue.replace(/[^\d,]/g, "");
      return Number(digitString.replace(",", "."));
    },
    isActive(alloy) {
      if (
        this.activeAlloy &&
        this.activeAlloy.brandName === alloy.brandName &&
        this.activeAlloy.temper === alloy.temper
      )
        return true;
      return false;
    },
    addActiveAlloy(alloy) {
      if (
        this.activeAlloy.brandName === alloy.brandName &&
        this.activeAlloy.temper === alloy.temper
      )
        this.activeAlloy = {};
      else this.activeAlloy = alloy;
    },
    scroll(direction) {
      var upPos = $(".dataSheet-wrapper").scrollTop();
      let value = 0;
      if (direction === "up") {
        value = upPos - 200;
      } else {
        value = upPos + 200;
      }
      $(".dataSheet-wrapper").animate(
        {
          scrollTop: value
        },
        "fast"
      );
    },
    exportPDF() {
      this.currentTime = new Date().toLocaleString("en-GB", {
        timeZone: "Europe/Paris"
      });
      this.$refs.html2Pdf.generatePdf();

      axios.defaults.baseURL = process.env.VUE_APP_ROOT_API;
      const token = process.env.VUE_APP_ROOT_API_GET_TOKEN;
      if (token) {
        axios.defaults.headers.common["Cockpit-Token"] = token;
        // axios({
        //   method: "post",
        //   url: "collections/save/logs?token=" + user.api_key,
        //   headers: { "Content-Type": "application/json" },
        //   data: JSON.stringify({
        //     data: {
        //       code: "EXP-PDF",
        //       action: "Export PDF",
        //       username: user.name,
        //       user_id: user._id,
        //       date: this.currentTime,
        //       infos: this.selectedAlloys
        //         .map(alloy => `${alloy.brandName} ${alloy.temper}`)
        //         .join(" - ")
        //     }
        //   })
        // }).then(resp => console.log(resp));
      }
    },
    onProgress: function(progress) {
      if (progress == 90) {
        this.isLoading = false;
      }
    },
    hasStartedGeneration: function() {
      this.isLoading = true;
    },
    getName() {
      return (
        "Export LBA - " +
        this.selectedAlloys.map(alloy => alloy.brandName).join("-")
      );
    },
    getOrderOfMagnitude(n) {
      var order = Math.floor(Math.log(n) / Math.LN10 + 0.000000001); // because float math sucks like that
      return Math.pow(10, order);
    }
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less">
@orange: #d36c48;
#datavisualisation {
  overflow-x: auto;
  overflow-y: hidden;
  margin-right: -15px;
  margin-left: -15px;
  padding: 4% 0;
  user-select: none;
  .header-section {
    width: 100%;
    display: flex;
    padding: 0 6.66% 3% 6.66%;
  }
  .tabs-selector-ctn {
    display: flex;
    flex-wrap: wrap;
    width: 35%;
    min-width: 200px;
    button:first-child {
      margin-right: 16px;
    }
  }
  .alloys-ctn {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
  }
  #datasheet-section {
    position: relative;
    min-width: 840px;
    padding: 0 6.66%;
    img.arrowUp,
    img.arrowDown {
      width: 24px;
      height: 24px;
      float: right;
      margin-right: -9px;
      &:hover {
        cursor: pointer;
      }
      &.arrowUp {
        transform: rotate(-90deg);
        margin-bottom: 8px;
      }
      &.arrowDown {
        transform: rotate(90deg);
        margin-top: 8px;
      }
    }
  }
  .dataSheet-wrapper {
    width: 100%;
    height: 70vh;
    min-width: 840px;
    overflow-y: scroll;
    overflow-x: hidden;
    &::-webkit-scrollbar {
      width: 5px;
      border-radius: 2px;
    }
    &::-webkit-scrollbar-track {
      border-radius: 2px;
      background-color: rgba(0, 0, 0, 0.1);
    }
    &::-webkit-scrollbar-thumb {
      border-radius: 2px;
      //padding: 0 0 0 100px;
      background-color: @orange;
    }
  }
  .tab-selector {
    margin: auto 0;
    border-radius: 50px;
    border: 1px solid gray;
    position: relative;
    &:before {
      content: "";
      position: absolute;
      bottom: -2px;
      left: 50%;
      right: 50%;
      height: 4px;
      background-color: @orange;
      transition: left 0.3s ease, right 0.3s ease;
    }
    &.active {
      border: 1px solid @orange;
      font-weight: bold;
      &:before {
        left: 30%;
        right: 30%;
      }
    }
    &:hover {
      border-color: @orange;
      &:before {
        left: 30%;
        right: 30%;
      }
    }
  }
  .graphic-wrapper {
    margin: 0 6.66%;
    padding: 16px 24px;
    background-color: rgba(255, 255, 255, 0.9);
    border-radius: 16px;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-evenly;
    .bold {
      font-weight: bold;
    }
    .my-radio {
      text-align: left;
    }
    input[type="radio"] {
      -webkit-appearance: none;
      background-color: #fafafa;
      border: 3px solid #b9b9b9;
      -webkit-box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05),
        inset 0px -15px 10px -12px rgba(0, 0, 0, 0.05);
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05),
        inset 0px -15px 10px -12px rgba(0, 0, 0, 0.05);
      width: 24px;
      height: 24px;
      padding: 9px;
      border-radius: 30px;
      display: inline-block;
      position: relative;
      margin-right: 5px;
      margin-top: -4px;
      vertical-align: middle;

      &:checked {
        border: 3px solid #d36c48;
      }
    }
  }
  .empty-alloy-item {
    padding: 4px;
    height: calc((33.33vw - 20px) / 4);
    width: calc((33.33vw - 20px) / 4);
    min-height: calc(480px / 4);
    min-width: calc(480px / 4);
    margin: 0 4px;
  }
  .itemAlloy {
    margin: 0 4px;
  }
  .dashed {
    border: 1px dashed @orange;
    border-radius: 10px;
    width: 100%;
    height: 100%;
  }
  .title {
    text-align: left;
    font-size: 1.3em;
    font-weight: bold;
  }
  .filter-arrow {
    transform: rotate(0);
  }
}
.main-container-pdf {
  background-color: #eaf5f9;
  text-align: center;
  font-size: 11px;
  height: 794px;
  section.header {
    padding-left: 5px;
    text-align: left;
    width: 100%;
    height: 5%;
    background-color: #541514;
    color: white;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 16px;
    #logo {
      width: 14%;
      align-self: center;
    }
  }
  section.message-ctn {
    width: 100%;
    height: 5%;
    background-color: rgba(255, 255, 255, 0.7);
    border-bottom: 1px white solid;
    color: darkslategrey;
  }
  section.central-ctn {
    display: flex;
    background-color: white;
    padding: 8px;
    border-radius: 8px;
    margin: 10px;
  }
  .alloys-ctn-pdf {
    width: 11%;
    .alloy-item {
      width: 130px;
      height: 130px;
      min-width: initial;
      min-height: initial;
    }
  }
  .graphs-ctn {
    display: flex;
    flex-wrap: wrap;
  }
  .graph-ctn {
    width: 33.33%;
    font-size: 8;
  }
  canvas {
    width: 100%;
  }
  section.footer {
    background-color: #541514;
    width: 100%;
    color: white !important;
  }
}
.html2pdf__overlay {
  z-index: -10 !important;
}
</style>
